import { TopicType } from "./SKArtifact";
import * as Yup from "yup";
import { ExternalLink } from "./SKChannel";
import { Timestamp } from "firebase/firestore";

export enum ApplicantType {
    Creator = 'creator',
    Brand = 'brand',
  }
  
  export enum EmployeeRange {
    One = '1',
    TwoToTen = '2 - 10',
    ElevenToFifty = '11 - 50',
    FiftyOneToTwoHundred = '51 - 200',
    TwoHundredToThousand = '200 - 1000',
    ThousandPlus = '>1000',
  }
  
  export enum TeamSize {
    TwoToFive = '2 - 5',
    SixToTen = '6 - 10',
    ElevenToFifty = '11 - 50',
    FiftyPlus = '50+',
  }
  
  export enum AudienceSize {
    Under100 = '<100',
    HundredTo1K = '100 - 1K',
    OneKTo10K = '1K - 10K',
    TenKTo100K = '10K - 100K',
    HundredKTo1M = '100K - 1M',
    OneMPlus = '1M+',
  }
  
  export enum Frequency {
    Daily = 'Daily',
    Weekly = 'Weekly',
    Monthly = 'Monthly',
  }
  
  export enum AudienceAgeRange {
    AllAges = 'All Ages or General Audience',
    Children = 'Young children (0 - 8 years)',
    PreTeens = 'Tween and Pre-teens (9 - 12 years)',
    Teenagers = 'Teenagers (13 - 17 years)',
    Adults = 'Adults (18+)',
  }
  
  // Interface for form state
  export interface SocialLink {
    platform: string;
    url: string;
  }

  export enum SensitiveTopic {
    None = 'None',
    MentalHealth = 'Mental health',
    HealthAdvice = 'Health advice',
    Religion = 'Religion',
    Politics = 'Politics',
    News = 'News',
    SubstanceUse = 'Substance use',
    SexualContent = 'Sexual Content',
    Violence = 'Violence',
    CriminalBehavior = 'Criminal Behavior',
    Profanity = 'Profanity or explicit language',
    Gambling = 'Gambling or betting related content',
    GenderSex = 'Gender and sex',
    Immigration = 'Immigration',
    WarConflict = 'War, conflict, and military',
    Finance = 'Finance, investing, or cryptocurrency',
    Fundraising = 'Cause based Fundraising',
    RaceEthnicity = 'Race and ethnicity',
    GriefLoss = 'Grief and loss',
    Activism = 'Social or political activism',
  }
  
  export enum ApplicationState {
    DRAFT = "DRAFT",
    SUBMITTED = "SUBMITTED",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED"
  }

  export enum ApplicationActionType {
    submitted = "Submitted",
    rejected = "Rejected",
    approved = "Approved",
  }

  export enum ActionItem {
    approve = "Approve Application",
    reject = "Reject Application",
  }
    export interface ActionOnItem {
        itemid: string;
        action: ActionItem;
    }
  
    export const actionToStateMap = (action: ActionItem)=> {
        switch (action) {
            case ActionItem.approve:
                return ApplicationState.APPROVED
            case ActionItem.reject:
                return ApplicationState.REJECTED
   
        }
    }
  export interface ApplicationForm {
    id?:string
    fullName: string;
    email: string;
    applicantType: ApplicantType ;
    legalEntityName?: string;
    numEmployees?: EmployeeRange; // required if ApplicantType.brand
    individualOrTeam?: 'individual' | 'team' ; // required if ApplicantType.creator
    numTeamMembers?: TeamSize; // required if ApplicantType.creator
    website?: string; // required if ApplicantType.brand
    country: string;
    bio: string;
    socialLinks: ExternalLink[];
    audienceSize: AudienceSize;
    banned: boolean;
    bannedExplain?: string; // required if banned
    channelName: string;
    channelSummery: string;
    contentConcept: string;
    contentType: string;
    frequency: Frequency ;
    audienceAge: AudienceAgeRange;
    primaryCategory: TopicType;
    secondaryCategories: TopicType[];
    primaryLanguage: string;
    audienceLocation: string;
    whySkarvo: string;
    valueToFollowers: string;
    engagementStrategy: string;
    onlineSafety: string;
    sensitiveTopics?: SensitiveTopic[];
    hasCollaborators: boolean ;
    collaboratorsExplain?: string;
    moderationGuidelines: string; // required if hasCollaborators
    hasRunPaidCommunity: boolean;
    paidCommunityExplain?: string; // required if hasRunPaidCommunity
    marketingStrategy: string;
    promotionStrategy: string;
    thirdPartySponsorships: boolean;
    sponsorExplain?: string; 
    skarvoPaidAds: boolean;
    interestedInPartnerships: boolean;
    agreesToGuidelines: boolean;
    confirmsLegalCompliance: boolean;
    contentIsSafe: boolean ;
    referralSource: string;
    status: ApplicationState;
    createdDate?: Timestamp;
    updatedDate?: Timestamp;
  }

  export const applicationFormFieldsOrdered: (keyof ApplicationForm)[] = [
    'fullName',
    'email',
    'applicantType',
    'legalEntityName',
    'numEmployees',
    'individualOrTeam',
    'numTeamMembers',
    'website',
    'country',
    'bio',
    'socialLinks',
    'audienceSize',
    'banned',
    'bannedExplain',
    'channelName',
    'channelSummery',
    'contentConcept',
    'contentType',
    'frequency',
    'audienceAge',
    'primaryCategory',
    'secondaryCategories',
    'primaryLanguage',
    'audienceLocation',
    'whySkarvo',
    'valueToFollowers',
    'engagementStrategy',
    'onlineSafety',
    'sensitiveTopics',
    'hasCollaborators',
    'collaboratorsExplain',
    'moderationGuidelines',
    'hasRunPaidCommunity',
    'paidCommunityExplain',
    'marketingStrategy',
    'promotionStrategy',
    'thirdPartySponsorships',
    'sponsorExplain',
    'skarvoPaidAds',
    'interestedInPartnerships',
    'agreesToGuidelines',
    'confirmsLegalCompliance',
    'contentIsSafe',
    'referralSource'
  ];

 
  export const ApplicationFormSchema = Yup.object().shape({
    id: Yup.string().notRequired(),
    fullName: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    applicantType: Yup.mixed<ApplicantType>().oneOf(Object.values(ApplicantType)).required(),
  
    // Conditional: if Brand
    legalEntityName: Yup.string().when('applicantType', {
      is: ApplicantType.Brand,
      then: (schema) => schema.required('Legal entity name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    numEmployees: Yup.mixed<EmployeeRange>().when('applicantType', {
      is: ApplicantType.Brand,
      then: (schema) => schema.required('Number of employees is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    website: Yup.string().when('applicantType', {
      is: ApplicantType.Brand,
      then: (schema) => schema.required('Website is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  
    // Conditional: if Creator
    individualOrTeam: Yup.string().when('applicantType', {
      is: ApplicantType.Creator,
      then: (schema) => schema.required('Select individual or team'),
      otherwise: (schema) => schema.notRequired(),
    }),
    numTeamMembers: Yup.mixed<TeamSize>().when(['applicantType', 'individualOrTeam'], {
      is: (type: ApplicantType, group: string) => type === ApplicantType.Creator && group === 'team',
      then: (schema) => schema.required('Team size is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  
    country: Yup.string().required('Country is required'),
    bio: Yup.string().max(500).required('Bio is required'),
  
    socialLinks: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string().required('Platform is required'),
          value: Yup.string().notRequired(),
        })
      )
      .min(1, 'At least one social link is required')
      .test('at-least-one-url', 'At least one URL must be provided', (socialLinks) => {
        if (!socialLinks) return false;
        return socialLinks.some(link => !!link.value && link.value.trim() !== '');
      }),
  
    audienceSize: Yup.mixed<AudienceSize>().oneOf(Object.values(AudienceSize)).required(),
  
    banned: Yup.boolean().required(),
    bannedExplain: Yup.string().when('banned', {
      is: true,
      then: (schema) => schema.required('Please tell us more'),
      otherwise: (schema) => schema.notRequired(),
    }),
  
    channelName: Yup.string().max(50).required("Channel name is required"),
    channelSummery: Yup.string().max(100).required("Channel summery is required"),
    contentConcept: Yup.string().max(200).required("Channel concept detail is required"),
    contentType: Yup.string().max(50).required("Content type is required"),
  
    frequency: Yup.mixed<Frequency>().oneOf(Object.values(Frequency)).required("Posting frequency is required"),
    audienceAge: Yup.mixed<AudienceAgeRange>().oneOf(Object.values(AudienceAgeRange)).required("Audiance age is required"),
  
    primaryCategory: Yup.mixed<TopicType>().oneOf(Object.values(TopicType)).required(),
    secondaryCategories: Yup.array()
      .of(Yup.mixed<TopicType>().oneOf(Object.values(TopicType)))
      .min(1, 'At least one secondary category is required'),
  
    primaryLanguage: Yup.string().required(),
    audienceLocation: Yup.string().max(100).required(),
  
    whySkarvo: Yup.string().max(200).required("Why Skarvo is required"),
    valueToFollowers: Yup.string().max(200).required("How channel value to followers required"),
    engagementStrategy: Yup.string().max(200).required("Engagement Strategy is required"),
    onlineSafety: Yup.string().max(200).required("Online safety is required"),
  
    sensitiveTopics: Yup.array()
      .of(Yup.mixed<SensitiveTopic>().oneOf(Object.values(SensitiveTopic)))
      .notRequired(),
  
    hasCollaborators: Yup.boolean().required(),
    collaboratorsExplain: Yup.string().when('hasCollaborators', {
        is: true,
        then: (schema) => schema.required('Please explain your collaborator experience'),
        otherwise: (schema) => schema.notRequired(),
      }),

    moderationGuidelines: Yup.string().required('Moderation guidelines are required'),
  
    hasRunPaidCommunity: Yup.boolean().required(),
    paidCommunityExplain: Yup.string().when('hasRunPaidCommunity', {
      is: true,
      then: (schema) => schema.required('Please explain your paid community experience'),
      otherwise: (schema) => schema.notRequired(),
    }),
  
    marketingStrategy: Yup.string().max(200).required(),
    promotionStrategy: Yup.string().max(200).required(),
  
    thirdPartySponsorships: Yup.boolean().required(),
    sponsorExplain: Yup.string().when('thirdPartySponsorships', {
      is: true,
      then: (schema) => schema.required('Please explain the third party sponsorship'),
      otherwise: (schema) => schema.notRequired(),
    }),
    skarvoPaidAds: Yup.boolean().required(),
    interestedInPartnerships: Yup.boolean().required(),
  
    referralSource: Yup.string().max(100).required(),
    agreesToGuidelines: Yup.boolean().oneOf([true], 'You must agree to the guidelines'),
    confirmsLegalCompliance: Yup.boolean().oneOf([true], 'You must confirm legal compliance'),
    contentIsSafe: Yup.boolean().oneOf([true], 'You must confirm your content is safe'),
    contentPurpose: Yup.boolean().oneOf([true], 'You must confirm your content has a valid purpose'),
    status: Yup.mixed<ApplicationState>().oneOf(Object.values(ApplicationState)).required(),
    createdDate: Yup.mixed<Timestamp>().notRequired(),
    updatedDate: Yup.mixed<Timestamp>().notRequired()
  });